/**
 * Dark / light theme selection.
 *
 * The choice lives in localStorage and is applied as `data-theme` on the
 * document root, which the CSS in index.css keys off. First-time visitors get
 * their OS preference (`prefers-color-scheme`); once they toggle, that explicit
 * choice sticks. Dark stays the default when nothing else is known — this runs
 * on a second monitor next to a live stream.
 */
import { useCallback, useEffect, useState } from "react";

const KEY = "quelby.theme";

export type Theme = "dark" | "light";

function initialTheme(): Theme {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved === "dark" || saved === "light") return saved;
  } catch {
    /* private mode / storage disabled — fall through to system preference */
  }
  try {
    if (window.matchMedia("(prefers-color-scheme: light)").matches) return "light";
  } catch {
    /* matchMedia unavailable */
  }
  return "dark";
}

export function useTheme(): [Theme, () => void] {
  const [theme, setTheme] = useState<Theme>(initialTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(KEY, theme);
    } catch {
      /* non-fatal — selection just won't persist */
    }
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((t) => (t === "dark" ? "light" : "dark"));
  }, []);

  return [theme, toggle];
}
